
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, CardBody, Form, FormGroup, Label, Input, Button,
    Row, Col, Alert
} from 'reactstrap';
import api from '../../services/api';
import toast from 'react-hot-toast';

const ExpenseForm = ({ onSuccess, onCancel, initialData = null }) => {
    const [currentMode, setCurrentMode] = useState(initialData?.mode || (initialData?.payment_status === 'PAGADO' ? 'payment' : 'payment')); // Default to payment if new
    // If we are editing a Pending one, default is 'schedule'
    // BUT we need to sync this with initialData better.
    // Let's rely on payment_status from formData eventually.

    const isEditing = !!initialData?.id;
    const isPaymentMode = currentMode === 'payment';

    const getColombiaDate = () => {
        return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
    };

    const [formData, setFormData] = useState({
        date: initialData?.date ? initialData.date.split('T')[0] : '',
        provider_name: initialData?.provider_name || '',
        provider_invoice_number: initialData?.provider_invoice_number || '',
        siigo_fc_number: initialData?.siigo_fc_number || '',
        amount: initialData?.amount || '',

        payment_date: initialData?.payment_date ? initialData.payment_date.split('T')[0] : getColombiaDate(),
        source: initialData?.source || (initialData?.payment_status === 'PAGADO' ? 'bancolombia' : ''),
        payment_status: initialData?.payment_status || 'PAGADO',
        siigo_status: initialData?.siigo_status || 'PENDIENTE',
        siigo_rp_number: initialData?.siigo_rp_number || '',

        cost_center: initialData?.cost_center || '',
        concept: initialData?.concept || '',
        description: initialData?.description || '',

        evidence: null,
        category: initialData?.category || '',
        bancolombia_movement_id: null,
        generate_siigo_rp: false
    });

    // Siigo Intelligence
    const liveSiigoBalance = initialData?.live_siigo_balance;
    const isLiveBalanceAvailable = liveSiigoBalance !== undefined && liveSiigoBalance !== null;
    const isCreditNoteApplied = isLiveBalanceAvailable && formData.amount && liveSiigoBalance < formData.amount;

    // === BANCOLOMBIA MOVEMENT LINKING ===
    const [bancoSearchOpen, setBancoSearchOpen] = useState(false);
    const [bancoMovements, setBancoMovements] = useState([]);
    const [bancoLoading, setBancoLoading] = useState(false);
    const [bancoSearchText, setBancoSearchText] = useState('');
    const [linkedMovement, setLinkedMovement] = useState(null);

    const fetchBancoMovements = useCallback(async (searchQ = '') => {
        setBancoLoading(true);
        try {
            const params = new URLSearchParams();
            if (formData.amount) params.append('amount', formData.amount);
            if (formData.payment_date) params.append('date', formData.payment_date);
            if (searchQ) params.append('q', searchQ);
            if (isEditing && initialData?.id) params.append('expense_id', initialData.id);

            const response = await api.get(`/expenses/bancolombia-movements?${params.toString()}`);
            if (response.data.success) {
                setBancoMovements(response.data.data || []);
                // Si estamos editando, detectar el movimiento ya vinculado
                const linked = (response.data.data || []).find(m => m.is_linked);
                if (linked && !linkedMovement) {
                    setLinkedMovement(linked);
                    setFormData(prev => ({ ...prev, bancolombia_movement_id: linked.id }));
                }
            }
        } catch (error) {
            console.error('Error fetching Bancolombia movements:', error);
        } finally {
            setBancoLoading(false);
        }
    }, [formData.amount, formData.payment_date, isEditing, initialData?.id]);

    // Auto-cargar movimientos vinculados al editar un egreso con source=bancolombia
    useEffect(() => {
        if (isEditing && initialData?.source === 'bancolombia') {
            fetchBancoMovements();
        }
    }, [isEditing, initialData?.source]);

    // Pre-cargar movimiento vinculado desde los datos del egreso (JOIN en getExpenses)
    useEffect(() => {
        if (isEditing && initialData?.linked_movement_id && !linkedMovement) {
            setLinkedMovement({
                id: initialData.linked_movement_id,
                fecha: initialData.linked_movement_fecha,
                descripcion: initialData.linked_movement_desc,
                valor: initialData.linked_movement_valor,
                is_linked: true
            });
            setFormData(prev => ({ ...prev, bancolombia_movement_id: initialData.linked_movement_id }));
        }
    }, [isEditing, initialData?.linked_movement_id]);

    const handleLinkMovement = (movement) => {
        setLinkedMovement(movement);
        setFormData(prev => ({ ...prev, bancolombia_movement_id: movement.id }));
        toast.success(`Movimiento vinculado: $${Math.abs(movement.valor).toLocaleString('es-CO')}`);
    };

    const handleUnlinkMovement = async () => {
        if (isEditing && initialData?.id) {
            try {
                await api.delete(`/expenses/${initialData.id}/bancolombia-link`);
            } catch (err) {
                console.warn('Error desvinculando:', err);
            }
        }
        setLinkedMovement(null);
        setFormData(prev => ({ ...prev, bancolombia_movement_id: null }));
        toast.success('Movimiento desvinculado');
    };

    const currencyFmtBanco = (v) => `$${Math.round(Math.abs(parseFloat(v) || 0)).toLocaleString('es-CO')}`;

    // Sync mode with payment_status changes
    const handlePaymentStatusChange = (e) => {
        const status = e.target.value;
        setFormData(prev => ({ ...prev, payment_status: status }));
        if (status === 'PAGADO') {
            setCurrentMode('payment');
        } else {
            setCurrentMode('schedule');
        }
    };

    // Initialize mode correctly
    React.useEffect(() => {
        if (initialData?.payment_status === 'PENDIENTE') {
            setCurrentMode('schedule');
        } else if (initialData?.payment_status === 'PAGADO') {
            setCurrentMode('payment');
        }
    }, [initialData]);

    const switchToPaymentMode = () => {
        setCurrentMode('payment');
        setFormData(prev => ({
            ...prev,
            payment_status: 'PAGADO',
            payment_date: getColombiaDate(),
            source: prev.source || 'bancolombia'
        }));
        toast.success('Modo cambiado a Registro de Pago');
    };

    // Set preview URL if editing and evidence exists (assuming we can't preview remote URL easily as Blob without fetch, 
    // but we can show a link or just "Existing File" placeholder. For now, we leave preview empty unless new file selected)
    // If we wanted to show existing image: 
    // const [previewUrl, setPreviewUrl] = useState(initialData?.evidence_url ? `${process.env.REACT_APP_API_URL}${initialData.evidence_url}` : null);
    // But let's stick to simple logic for now.
    const [previewUrl, setPreviewUrl] = useState(initialData?.evidence_url ? (initialData.evidence_url.startsWith('http') ? initialData.evidence_url : `${process.env.REACT_APP_API_URL}${initialData.evidence_url}`) : null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // COST CENTERS from Excel
    const COST_CENTERS = [
        'ADMINISTRACIÓN',
        'VENTAS Y PUBLICIDAD',
        'LOGISTICA Y DISTRIBUCIÓN',
        'CONTABILIDAD',
        'MANTENIMIENTO',
        'INNOVACION',
        'OTROS GASTOS',
        'PROVEEDOR',
        'TESORERIA'
    ];

    const EXPENSE_CATEGORIES = [
        'ARRIENDO',
        'EXTRA-EMPLEADOS',
        'MARCA',
        'PRODUCTO POPPING',
        'PRODUCTO FLAVOR',
        'PRODUCTO REVENTA',
        'SALARIO',
        'TRANSPORTE-CLIENTES',
        'TRANSPORTE-DISTRIBUIDORES',
        'DEVOLUCION DE FLETE',
        'DEVOLUCION DE PRODUCTO',
        'OTROS'
    ];

    const SOURCES = [
        { value: 'bancolombia', label: 'Bancolombia' },
        { value: 'mercadopago', label: 'Mercado Pago' },
        { value: 'caja_menor', label: 'Caja Menor' }
    ];

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData(prev => ({ ...prev, evidence: file }));
        if (file) {
            setPreviewUrl(URL.createObjectURL(file));
        } else {
            setPreviewUrl(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // Validations
        // Validations
        // Ensure at least one date is present (Invoice Date OR Payment Date)
        const finalDate = formData.date || formData.payment_date;

        if (!finalDate) {
            setError('Debe registrar al menos una fecha (Factura o Pago).');
            return;
        }

        if (isPaymentMode && !formData.source) {
            setError('Para registrar un pago realizado, debe seleccionar la Fuente de dinero.');
            return;
        }

        if (!formData.amount || !formData.cost_center || !formData.category) {
            setError('Por favor complete todos los campos obligatorios (*).');
            return;
        }

        if (isPaymentMode && !previewUrl) {
            setError('Para registrar un pago realizado, ES OBLIGATORIO adjuntar el comprobante/evidencia.');
            return;
        }

        setLoading(true);
        const data = new FormData();
        // Append all fields
        // Append all fields
        Object.keys(formData).forEach(key => {
            if (formData[key] !== null && key !== 'evidence') { // Handle evidence and nulls
                data.append(key, formData[key]);
            }
        });

        // Force payment_status based on mode if creating new (sanity check)
        if (!isEditing) {
            data.set('payment_status', isPaymentMode ? 'PAGADO' : 'PENDIENTE');
        }

        if (formData.evidence) {
            data.append('evidence', formData.evidence);
        }



        try {
            let expenseId = isEditing ? initialData.id : null;
            if (isEditing) {
                await api.put(`/expenses/${initialData.id}`, data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Gasto actualizado exitosamente');
            } else {
                const res = await api.post('/expenses', data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                expenseId = res.data.expenseId || res.data.data?.id;
                toast.success('Gasto registrado exitosamente');
            }

            // Mágica Sincronización RP Siigo
            if (formData.generate_siigo_rp && expenseId) {
                toast.loading('Generando Recibo de Pago en Siigo...', { id: 'rp-toast' });
                const rpRes = await api.post('/expenses/generate-siigo-rp', { expense_id: expenseId });
                toast.dismiss('rp-toast');
                if (rpRes.data.success) {
                    toast.success(`RP Generado exitosamente: ${rpRes.data.rp_number}`);
                } else {
                    toast.error(`Error generando RP: ${rpRes.data.message}`);
                }
            } else if (formData.generate_siigo_rp && !expenseId) {
                toast.error('No se pudo generar el RP: Faltó el ID local del gasto.');
            }

            if (onSuccess) onSuccess();
        } catch (err) {
            toast.dismiss('rp-toast');
            console.error(err);
            const serverMsg = err.response?.data?.message || err.message;
            setError(`Error al guardar el gasto: ${serverMsg}`);
            toast.error('Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const handlePaste = (e) => {
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const blob = items[i].getAsFile();
                setFormData(prev => ({ ...prev, evidence: blob }));
                setPreviewUrl(URL.createObjectURL(blob));
                toast.success('Imagen pegada del portapapeles');
                break;
            }
        }
    };

    // === SIIGO PURCHASE IMPORT ===
    const [siigoSearchOpen, setSiigoSearchOpen] = useState(false);
    const [siigoSearchTerm, setSiigoSearchTerm] = useState('');
    const [siigoPurchases, setSiigoPurchases] = useState([]);
    const [siigoLoading, setSiigoLoading] = useState(false);

    const fetchSiigoPurchases = async (searchQ = '') => {
        setSiigoLoading(true);
        try {
            const params = new URLSearchParams();
            const now = new Date();
            params.append('month', now.getMonth() + 1);
            params.append('year', now.getFullYear());
            if (searchQ) params.append('q', searchQ);

            const response = await api.get(`/expenses/siigo-purchases?${params.toString()}`);
            if (response.data.success) {
                setSiigoPurchases(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching Siigo purchases:', error);
            toast.error('Error consultando Siigo');
        } finally {
            setSiigoLoading(false);
        }
    };

    const handleSiigoImport = (purchase) => {
        setFormData(prev => ({
            ...prev,
            date: purchase.date ? purchase.date.substring(0, 10) : prev.date,
            provider_name: purchase.supplier_name || prev.provider_name,
            siigo_fc_number: purchase.name || prev.siigo_fc_number,
            amount: purchase.total || prev.amount,
            siigo_status: 'APLICADO',
            concept: purchase.observations || (purchase.items?.map(i => i.description).join(', ')) || prev.concept
        }));
        setSiigoSearchOpen(false);
        toast.success(`FC ${purchase.name} importada desde Siigo`);
    };

    const currencyFmt = (v) => `$${Math.round(parseFloat(v) || 0).toLocaleString('es-CO')}`;

    return (
        <div onPaste={handlePaste} tabIndex="0" style={{ outline: 'none' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <span className="text-muted text-sm"><small>Tip: Pega (Ctrl+V) el comprobante {isPaymentMode && '(*Obligatorio)'} en cualquier lugar</small></span>
                {!isPaymentMode && (
                    <Button color="success" size="sm" onClick={switchToPaymentMode}>
                        <i className="ni ni-money-coins mr-2" />
                        ¡REALIZAR PAGO AHORA!
                    </Button>
                )}
            </div>
            {error && <Alert color="danger">{error}</Alert>}
            <Form onSubmit={handleSubmit}>

                {/* EVIDENCIA (Moved to Top) */}
                <div className="mb-4">
                    {!previewUrl ? (
                        <div
                            className="border rounded p-4 text-center position-relative"
                            style={{ border: '2px dashed #dee2e6', backgroundColor: '#fcfcfc', cursor: 'pointer' }}
                            onClick={() => document.getElementById('evidenceFile').click()}
                        >
                            <Input type="file" name="evidence" id="evidenceFile" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                            <i className="ni ni-cloud-upload-96 display-4 text-primary mb-2"></i>
                            <p className="small mb-0 text-muted font-weight-bold">
                                {isPaymentMode ? 'Evidencia de Pago Obligatoria (*)' : 'Click para subir evidencia o pega la imagen (Ctrl+V)'}
                            </p>
                        </div>
                    ) : (
                        <div className="text-center position-relative bg-light p-2 rounded border">
                            <img src={previewUrl} alt="Preview" className="img-fluid rounded shadow-sm" style={{ maxHeight: '200px' }} />
                            <Button size="sm" color="danger" className="position-absolute" style={{ top: '5px', right: '5px' }} onClick={() => { setPreviewUrl(null); setFormData(p => ({ ...p, evidence: null })); }}>
                                <i className="ni ni-fat-remove" />
                            </Button>
                        </div>
                    )}
                </div>

                {/* SECCIÓN 1: CAUSACIÓN (SIIGO) - Removed bg-secondary */}
                <div className="border p-3 rounded mb-3 bg-white shadow-sm">
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h6 className="heading-small text-primary mb-0">1. Causación (Datos Factura)</h6>
                        {!isEditing && (
                            <Button
                                color="info"
                                size="sm"
                                outline
                                onClick={() => {
                                    setSiigoSearchOpen(!siigoSearchOpen);
                                    if (!siigoSearchOpen && siigoPurchases.length === 0) {
                                        fetchSiigoPurchases();
                                    }
                                }}
                            >
                                <i className="ni ni-cloud-download-95 mr-1" />
                                {siigoSearchOpen ? 'Cerrar' : 'Importar desde Siigo'}
                            </Button>
                        )}
                    </div>

                    {/* SIIGO IMPORT PANEL */}
                    {siigoSearchOpen && (
                        <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#f0f7ff', borderColor: '#b3d4fc' }}>
                            <div className="d-flex align-items-center mb-2">
                                <Input
                                    bsSize="sm"
                                    type="text"
                                    placeholder="Buscar por proveedor, # FC, monto..."
                                    className="mr-2"
                                    style={{ maxWidth: '300px' }}
                                    value={siigoSearchTerm}
                                    onChange={(e) => setSiigoSearchTerm(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            fetchSiigoPurchases(siigoSearchTerm);
                                        }
                                    }}
                                />
                                <Button size="sm" color="info" onClick={() => fetchSiigoPurchases(siigoSearchTerm)} disabled={siigoLoading}>
                                    {siigoLoading ? 'Cargando...' : 'Buscar'}
                                </Button>
                            </div>

                            {siigoLoading ? (
                                <div className="text-center p-2 text-muted">Consultando Siigo...</div>
                            ) : siigoPurchases.length === 0 ? (
                                <div className="text-center p-2 text-muted">No se encontraron facturas de compra</div>
                            ) : (
                                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                    <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.78rem' }}>
                                        <thead>
                                            <tr>
                                                <th># FC</th>
                                                <th>Proveedor</th>
                                                <th className="text-right">Total</th>
                                                <th>Fecha</th>
                                                <th className="text-center">Estado</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {siigoPurchases.map((p, idx) => (
                                                <tr key={idx} style={p.already_imported ? { opacity: 0.5 } : {}}>
                                                    <td className="font-weight-bold">{p.name || '-'}</td>
                                                    <td>{p.supplier_name}</td>
                                                    <td className="text-right font-weight-bold text-dark">{currencyFmt(p.total)}</td>
                                                    <td>{p.date ? p.date.substring(0, 10) : '-'}</td>
                                                    <td className="text-center">
                                                        {p.already_imported ? (
                                                            <span className="badge badge-success" style={{ fontSize: '9px' }}>REGISTRADA</span>
                                                        ) : (
                                                            <span className="badge badge-warning" style={{ fontSize: '9px' }}>NUEVA</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {!p.already_imported && (
                                                            <Button
                                                                size="sm"
                                                                color="success"
                                                                className="py-0 px-2"
                                                                onClick={() => handleSiigoImport(p)}
                                                                style={{ fontSize: '11px' }}
                                                            >
                                                                Usar
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    <Row>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="date" className="form-control-label text-xs">Fecha Factura</Label>
                                <Input bsSize="sm" type="date" name="date" id="date" value={formData.date} onChange={handleInputChange} />
                            </FormGroup>
                        </Col>
                        <Col md={9}>
                            <FormGroup>
                                <Label for="provider_name" className="form-control-label text-xs">Nombre del Proveedor</Label>
                                <Input bsSize="sm" type="text" name="provider_name" id="provider_name" placeholder="Ej: Google Cloud" value={formData.provider_name} onChange={handleInputChange} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="provider_invoice_number" className="form-control-label text-xs">Factura Proveedor</Label>
                                <Input bsSize="sm" type="text" name="provider_invoice_number" id="provider_invoice_number" placeholder="Ej: FE-123" value={formData.provider_invoice_number} onChange={handleInputChange} />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="siigo_fc_number" className="form-control-label text-xs">FC en Siigo</Label>
                                <Input bsSize="sm" type="text" name="siigo_fc_number" id="siigo_fc_number" placeholder="Ej: FC-1-4556" value={formData.siigo_fc_number} onChange={handleInputChange} />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="amount" className="form-control-label is-required text-xs">Valor Total</Label>
                                <Input bsSize="sm" type="number" step="0.01" name="amount" id="amount" placeholder="0" value={formData.amount} onChange={handleInputChange} required />
                            </FormGroup>
                        </Col>
                    </Row>
                </div>

                {/* SECCIÓN 2: SALIDA DE DINERO */}
                <div className="border p-3 rounded mb-3 bg-white shadow-sm">
                    <h6 className="heading-small text-primary mb-3 border-bottom pb-2">2. Salida de Dinero (Pago)</h6>
                    
                    {/* SIIGO INTELLIGENCE PANEL */}
                    {isLiveBalanceAvailable && (
                        <div className={`p-3 mb-3 rounded border ${isCreditNoteApplied ? 'bg-warning-light border-warning' : 'bg-light border-light'}`} style={isCreditNoteApplied ? {backgroundColor: '#fffcf5'} : {}}>
                            <div className="d-flex align-items-center">
                                <i className={`ni ${isCreditNoteApplied ? 'ni-bell-55 text-warning' : 'ni-check-bold text-success'} mr-2`} style={{fontSize: '1.5rem'}}></i>
                                <div>
                                    <h5 className="mb-0">
                                        Inteligencia Siigo: Saldo Actual <strong>{currencyFmtBanco(liveSiigoBalance)}</strong>
                                    </h5>
                                    {isCreditNoteApplied ? (
                                        <p className="text-sm mb-0 text-muted mt-1">
                                            <strong>¡Atención!</strong> El saldo en Siigo es MENOR al total de la factura ({currencyFmtBanco(formData.amount)}). 
                                            Es probable que ya exista una <strong>Nota Crédito</strong> u otro pago.
                                        </p>
                                    ) : (
                                        <p className="text-sm mb-0 text-muted mt-1">
                                            El saldo en Siigo coincide con el total. No se detectan Notas Crédito previas.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <Row>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="payment_date" className="form-control-label text-xs">
                                    {isPaymentMode ? 'Fecha Real Pago' : 'Fecha Tentativa Pago'}
                                </Label>
                                <Input bsSize="sm" type="date" name="payment_date" id="payment_date" value={formData.payment_date} onChange={handleInputChange} />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="source" className="form-control-label text-xs">
                                    {isPaymentMode ? 'Banco / Fuente (*)' : 'Fuente (Opcional)'}
                                </Label>
                                <Input bsSize="sm" type="select" name="source" id="source" value={formData.source} onChange={handleInputChange}>
                                    <option value="">-- {isPaymentMode ? 'Seleccione' : 'Por Definir'} --</option>
                                    {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={2}>
                            <FormGroup>
                                <Label for="siigo_status" className="form-control-label text-xs">Registro en Siigo</Label>
                                <Input bsSize="sm" type="select" name="siigo_status" id="siigo_status" value={formData.siigo_status} onChange={handleInputChange}>
                                    <option value="PENDIENTE">PENDIENTE</option>
                                    <option value="APLICADO">APLICADO</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="payment_status" className="form-control-label text-xs">Estado Pago</Label>
                                <Input bsSize="sm" type="select" name="payment_status" id="payment_status" value={formData.payment_status} onChange={handlePaymentStatusChange}>
                                    <option value="PENDIENTE">PENDIENTE</option>
                                    <option value="PAGADO">PAGADO</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    
                    <Row className="mb-3">
                        <Col md={12}>
                            <div className="custom-control custom-checkbox pt-2 border-top mt-1">
                                <Input 
                                    type="checkbox" 
                                    className="custom-control-input" 
                                    id="generate_siigo_rp" 
                                    name="generate_siigo_rp"
                                    checked={formData.generate_siigo_rp}
                                    onChange={handleInputChange}
                                />
                                <Label className="custom-control-label font-weight-bold text-primary" htmlFor="generate_siigo_rp">
                                    ⚡ Generar Comprobante de Egreso (RP) automáticamente en Siigo
                                </Label>
                                <p className="text-muted text-xs ml-4 mt-1 mb-0">
                                    Al guardar, internamente se creará el Recibo de Pago cruzándolo contra la factura <strong>{formData.siigo_fc_number}</strong>.
                                    Ideal para Bancolombia o Caja Menor.
                                </p>
                            </div>
                        </Col>
                    </Row>

                    {/* BANCOLOMBIA MOVEMENT LINKING PANEL */}
                    {formData.source === 'bancolombia' && (
                        <div className="mt-3">
                            {/* Linked Movement Badge */}
                            {linkedMovement ? (
                                <div className="d-flex align-items-center justify-content-between p-2 rounded border" style={{ backgroundColor: '#e8f5e9', borderColor: '#66bb6a' }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge badge-success" style={{ fontSize: '10px' }}>✓ VINCULADO</span>
                                        <span className="text-sm font-weight-bold text-dark">
                                            {linkedMovement.fecha} — {linkedMovement.descripcion}
                                        </span>
                                        <span className="text-sm font-weight-bold text-success ml-2">
                                            {currencyFmtBanco(linkedMovement.valor)}
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-1">
                                        <Button size="sm" color="danger" outline className="py-0 px-2" style={{ fontSize: '11px' }} onClick={handleUnlinkMovement}>
                                            Desvincular
                                        </Button>
                                        <Button size="sm" color="info" outline className="py-0 px-2" style={{ fontSize: '11px' }} onClick={() => { setBancoSearchOpen(!bancoSearchOpen); if (!bancoSearchOpen) fetchBancoMovements(); }}>
                                            Cambiar
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <Button
                                    size="sm"
                                    color="info"
                                    outline
                                    className="w-100"
                                    onClick={() => { setBancoSearchOpen(!bancoSearchOpen); if (!bancoSearchOpen) fetchBancoMovements(); }}
                                >
                                    <i className="ni ni-credit-card mr-1" />
                                    {bancoSearchOpen ? 'Cerrar búsqueda' : '🔗 Vincular Movimiento Bancolombia'}
                                </Button>
                            )}

                            {/* Search Panel */}
                            {bancoSearchOpen && (
                                <div className="border rounded p-3 mt-2" style={{ backgroundColor: '#fff8e1', borderColor: '#ffcc02' }} onPaste={handlePaste}>
                                    <div className="d-flex align-items-center mb-2">
                                        <Input
                                            bsSize="sm"
                                            type="text"
                                            placeholder="Buscar por descripción..."
                                            className="mr-2"
                                            style={{ maxWidth: '300px' }}
                                            value={bancoSearchText}
                                            onChange={(e) => setBancoSearchText(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    fetchBancoMovements(bancoSearchText);
                                                }
                                            }}
                                        />
                                        <Button size="sm" color="warning" onClick={() => fetchBancoMovements(bancoSearchText)} disabled={bancoLoading}>
                                            {bancoLoading ? 'Buscando...' : 'Buscar'}
                                        </Button>
                                        <Button size="sm" color="secondary" outline className="ml-1" onClick={() => { setBancoSearchText(''); fetchBancoMovements(''); }}>
                                            Todos
                                        </Button>
                                    </div>
                                    <small className="text-muted d-block mb-2">
                                        Mostrando débitos ±10% del monto ({currencyFmtBanco(formData.amount || 0)}) ±5 días de la fecha de pago
                                    </small>

                                    {bancoLoading ? (
                                        <div className="text-center p-2 text-muted">Consultando movimientos...</div>
                                    ) : bancoMovements.length === 0 ? (
                                        <div className="text-center p-2 text-muted">No se encontraron movimientos que coincidan</div>
                                    ) : (
                                        <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                            <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.78rem' }}>
                                                <thead>
                                                    <tr>
                                                        <th>Fecha</th>
                                                        <th>Descripción</th>
                                                        <th className="text-right">Valor</th>
                                                        <th>Doc</th>
                                                        <th className="text-center"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bancoMovements.map((m) => (
                                                        <tr key={m.id} style={m.is_linked ? { backgroundColor: '#e8f5e9' } : {}}>
                                                            <td className="text-nowrap">{(m.fecha || '').toString().substring(0, 10)}</td>
                                                            <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.descripcion}>
                                                                {m.descripcion}
                                                            </td>
                                                            <td className="text-right font-weight-bold text-danger">{currencyFmtBanco(m.valor)}</td>
                                                            <td>{m.documento || '-'}</td>
                                                            <td className="text-center">
                                                                {m.is_linked ? (
                                                                    <span className="badge badge-success" style={{ fontSize: '9px' }}>ACTUAL</span>
                                                                ) : (
                                                                    <Button
                                                                        size="sm"
                                                                        color="success"
                                                                        className="py-0 px-2"
                                                                        onClick={() => { handleLinkMovement(m); setBancoSearchOpen(false); }}
                                                                        style={{ fontSize: '11px' }}
                                                                    >
                                                                        Vincular
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* SECCIÓN 3: CLASIFICACIÓN */}
                <h6 className="heading-small text-primary mb-3 mt-3 border-bottom pb-2">3. Clasificación</h6>
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="cost_center" className="form-control-label is-required">Centro de Costo</Label>
                            <Input type="select" name="cost_center" id="cost_center" value={formData.cost_center} onChange={handleInputChange} required>
                                <option value="">Seleccione...</option>
                                {COST_CENTERS.map(c => <option key={c} value={c}>{c}</option>)}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="category" className="form-control-label is-required">Tipo de Gasto</Label>
                            <Input type="select" name="category" id="category" value={formData.category} onChange={handleInputChange} required>
                                <option value="">Seleccione...</option>
                                {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="concept" className="form-control-label">Concepto de Compra</Label>
                            <Input type="text" name="concept" id="concept" placeholder="Detalle del gasto..." value={formData.concept} onChange={handleInputChange} />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <FormGroup>
                            <Label for="description" className="form-control-label text-xs">Notas Administrativas / Seguimiento (Observaciones)</Label>
                            <Input type="textarea" name="description" id="description" rows="3" placeholder="Observaciones de pago, novedades, etc." value={formData.description} onChange={handleInputChange} />
                        </FormGroup>
                    </Col>
                </Row>




                <div className="text-right">
                    {onCancel && (
                        <Button color="secondary" type="button" onClick={onCancel} className="mr-2" disabled={loading}>
                            Cancelar
                        </Button>
                    )}
                    <Button color={isPaymentMode ? "success" : "warning"} type="submit" disabled={loading}>
                        {loading ? 'Guardando...' : (isEditing ? 'Actualizar Gasto' : (isPaymentMode ? 'Confirmar Pago' : 'Programar Pago'))}
                    </Button>
                </div>
            </Form>
        </div >
    );
};

export default ExpenseForm;
